import * as Sentry from '@sentry/react'
import type React from 'react'
import { createContext, useContext } from 'react'
import { type Session, useSession } from '@/hooks/chat/useSession'
import type {
  ChatMessage,
  ChatMessageWithTimestamp,
} from '@/types/chat/ChatMessage'

interface SessionContextValue {
  currentSession: Session
  isLoadingMessages: boolean
  hasMoreMessages: boolean
  isLoadingMoreMessages: boolean
  hasMorePreviousMessages: boolean
  isLoadingPreviousMessages: boolean
  isGuestMode: boolean
  loadSessions: () => Promise<void>
  switchSession: (sessionId: string) => Promise<void>
  saveMessages: (newMessages: ChatMessage[]) => Promise<void>
  renderMessages: (messages: ChatMessageWithTimestamp[]) => Promise<void>
  updateCurrentMessage: (message: ChatMessageWithTimestamp) => Promise<void>
  loadMoreMessages: () => Promise<void>
  loadPreviousMessages: () => Promise<void>
  migrateGuestSession: (guestMessages: ChatMessage[]) => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const sessionHook = useSession()

  return (
    <SessionContext.Provider value={sessionHook}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSessionContext(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) {
    const error = new Error(
      'useSessionContext must be used within a SessionProvider'
    )
    // Capture context usage errors in Sentry
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: { context_error: 'session_provider_missing' },
      })
    }
    throw error
  }
  return context
}
